import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { StickyNote, PhoneCall, Lock, LockOpen, Trash2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const CONTACT_METHODS = ["Text", "Phone call", "Email", "WhatsApp/Messenger", "In person", "Other"];

const TYPE_META = {
  note: { label: "Note", icon: StickyNote, cls: "bg-slate-100 text-slate-700" },
  contact: { label: "Contacted", icon: PhoneCall, cls: "bg-blue-100 text-blue-700" },
  lockout: { label: "Locked out of gate", icon: Lock, cls: "bg-red-100 text-red-700" },
  restore: { label: "Gate access restored", icon: LockOpen, cls: "bg-green-100 text-green-700" },
  admin: { label: "Admin change", icon: ShieldAlert, cls: "bg-purple-100 text-purple-700" },
};

export const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-NZ", { day: "2-digit", month: "short", hour: "numeric", minute: "2-digit" }) : "");

export const describeContact = (c) => (c ? `Contacted ${fmtWhen(c.created_at)} by ${c.by} · ${c.method}` : "");

// Returns true if staff want to go ahead despite a recent contact
export const confirmIfRecent = (row) => !row?.recently_contacted || window.confirm(`${describeContact(row.last_contact)}.\n\nContact them again anyway?`);

export const EventTimeline = ({ events, onChanged }) => {
  const { user } = useAuth();
  if (!events?.length) return <p className="text-xs text-slate-400 italic" data-testid="timeline-empty">No notes or contacts yet.</p>;
  const remove = async (id) => {
    if (!window.confirm("Delete this entry? Only do this if it was made by mistake.")) return;
    try { await api.adminDeleteEvent(id); toast.success("Entry deleted"); onChanged?.(); } catch { toast.error("Could not delete entry"); }
  };
  return (
    <ol className="space-y-2" data-testid="event-timeline">
      {events.map((e) => {
        const m = TYPE_META[e.type] || TYPE_META.note;
        const Icon = m.icon;
        return (
          <li key={e.id} className="flex gap-2 text-xs" data-testid={`timeline-event-${e.id}`}>
            <span className={`mt-0.5 h-6 w-6 shrink-0 rounded-full flex items-center justify-center ${m.cls}`}><Icon className="h-3.5 w-3.5" /></span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-800">{m.label}{e.method ? ` · ${e.method}` : ""} <span className="font-normal text-slate-500">— {e.by}, {fmtWhen(e.created_at)}</span></p>
              {e.text && <p className="text-slate-600 whitespace-pre-wrap break-words">{e.text}</p>}
              {e.balance > 0 && <p className="text-slate-400">Balance at the time: ${Number(e.balance).toFixed(2)}</p>}
            </div>
            {user?.role === "admin" && (
              <button onClick={() => remove(e.id)} className="text-slate-300 hover:text-red-500 self-start" title="Delete (admin)" data-testid={`timeline-delete-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
            )}
          </li>
        );
      })}
    </ol>
  );
};

export const ContactDialog = ({ open, onOpenChange, unitId, title, onDone, defaultMethod = "" }) => {
  const [method, setMethod] = useState(defaultMethod);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!method) { toast.error("Choose how you made contact"); return; }
    setBusy(true);
    try { await api.addUnitEvent(unitId, { type: "contact", method, text }); toast.success("Marked as contacted"); setText(""); setMethod(""); onOpenChange(false); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="contact-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader><DialogTitle>Mark as contacted</DialogTitle><DialogDescription>{title}</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          {CONTACT_METHODS.map((m) => (
            <button key={m} onClick={() => setMethod(m)} data-testid={`contact-method-${m.replace(/[^a-z]/gi, "").toLowerCase()}`}
              className={`rounded-md border px-3 py-2 text-sm text-left transition-colors ${method === m ? "border-blue-500 bg-blue-50 text-blue-800 font-medium" : "border-slate-200 hover:bg-slate-50"}`}>{m}</button>
          ))}
        </div>
        <Textarea data-testid="contact-note" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Short note (optional)" />
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button data-testid="contact-save" onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const NoteBox = ({ unitId, onDone }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.addUnitEvent(unitId, { type: "note", text }); toast.success("Note added"); setText(""); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add note"); } finally { setBusy(false); }
  };
  return (
    <div className="flex gap-2" data-testid="note-box">
      <Input data-testid="note-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a note, e.g. told us they'll miss this week" onKeyDown={(e) => e.key === "Enter" && save()} />
      <Button data-testid="note-save" onClick={save} disabled={busy || !text.trim()} variant="outline" className="shrink-0"><StickyNote className="h-4 w-4 mr-1" /> Add</Button>
    </div>
  );
};

export const LockoutButtons = ({ unit, balance = 0, onDone }) => {
  const [busy, setBusy] = useState(false);
  if (!unit) return null;
  const lock = async () => {
    const reason = window.prompt("Reason for gate lockout (optional):", balance > 0 ? `Overdue balance $${balance.toFixed(2)}` : "");
    if (reason === null) return;
    setBusy(true);
    try { await api.lockoutUnit(unit.id, reason); toast.success(`${unit.unit_number} marked locked out`); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  const restore = async () => {
    if (!window.confirm(`Confirm gate access has been turned back on for ${unit.unit_number}?`)) return;
    setBusy(true);
    try { await api.restoreUnitAccess(unit.id); toast.success("Gate access restored"); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return unit.gate_locked ? (
    <Button size="sm" variant="outline" onClick={restore} disabled={busy} className="gap-1 border-green-300 text-green-700 hover:bg-green-50" data-testid="gate-restore-btn"><LockOpen className="h-4 w-4" /> Gate access restored</Button>
  ) : (
    <Button size="sm" variant="outline" onClick={lock} disabled={busy || unit.status !== "occupied"} className="gap-1 border-red-300 text-red-700 hover:bg-red-50" data-testid="gate-lockout-btn"><Lock className="h-4 w-4" /> Locked out of gate</Button>
  );
};
